import React, { useEffect, useRef, useState } from 'react';
import { useLoadScript, Libraries } from "@react-google-maps/api";
import { GOOGLE_MAPS_API_KEY } from '@/shared/config/environment';
import { googleMapsVerifier } from '@/services/google-maps/google-maps-verifier';

const libraries: Libraries = ["places"];

interface GooglePlacesSearchProps {
  onAddressSelect: (address: string, city: string, postalCode: string, country: string, houseNumber: string, street: string) => void;
  placeholder?: string;
  autocompleteText?: string;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  onGoogleMapsLoadSuccess?: () => void;
  onGoogleMapsLoadFailure?: () => void;
  enableManualFallback?: boolean;
}

/**
 * Composant réutilisable pour la recherche d'adresses avec Google Places Autocomplete
 */
const GooglePlacesSearch: React.FC<GooglePlacesSearchProps> = ({
  onAddressSelect,
  placeholder = "Commencez à taper votre adresse...",
  autocompleteText = "Sélectionnez une adresse dans les suggestions",
  value = '',
  onChange,
  className = '',
  onGoogleMapsLoadSuccess,
  onGoogleMapsLoadFailure,
  enableManualFallback = true,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null); // Deprecated
  const sessionToken = useRef<any>(null);
  // const placesServiceDiv = useRef<HTMLDivElement>(null); // Not needed

  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [authError, setAuthError] = useState(false);
  const [failed, setFailed] = useState(false);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || "",
    libraries,
  });

  useEffect(() => {
    const originalAuthFailure = (window as any).gm_authFailure;
    (window as any).gm_authFailure = () => {
      console.log('❌ [GooglePlacesSearch] gm_authFailure déclenché');
      setAuthError(true);
      setFailed(true);
      if (onGoogleMapsLoadFailure) onGoogleMapsLoadFailure();
      if (originalAuthFailure) originalAuthFailure();
    };

    return () => {
      (window as any).gm_authFailure = originalAuthFailure;
    };
  }, [onGoogleMapsLoadFailure]);

  useEffect(() => {
    if (loadError) {
      console.error('❌ [GooglePlacesSearch] Erreur chargement Google Maps:', loadError);
      setFailed(true);
      if (onGoogleMapsLoadFailure) onGoogleMapsLoadFailure();
    } else if (isLoaded) {
      if (onGoogleMapsLoadSuccess) onGoogleMapsLoadSuccess();
    }
  }, [isLoaded, loadError, onGoogleMapsLoadFailure, onGoogleMapsLoadSuccess]);

  // Initialize services
  useEffect(() => {
    if (isLoaded && !sessionToken.current && window.google?.maps?.places) {
      // Initialization of sessionToken is handled in handleInputChange or lazily
      // We can pre-load the library here if we want, but handleInputChange does it too.
    }
  }, [isLoaded]);

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (onChange) {
      onChange(val);
    }

    if (!val) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (isLoaded && window.google?.maps) {
      try {
        // @ts-ignore - importLibrary might not be in types yet
        const { AutocompleteSuggestion, AutocompleteSessionToken } = await google.maps.importLibrary("places");
        
        if (!sessionToken.current) {
          sessionToken.current = new AutocompleteSessionToken();
        }

        const request = {
          input: val,
          sessionToken: sessionToken.current,
          includedRegionCodes: ['fr', 'be', 'ch', 'lu', 'es', 'de', 'it', 'nl', 'pt', 'gb'],
        };

        const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
        setSuggestions(suggestions || []);
        setShowSuggestions(true);
      } catch (error) {
        console.error('❌ [GooglePlacesSearch] Autocomplete error:', error);
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }
  };

  const handleSelectPrediction = async (placeId: string, description: string) => {
    if (onChange) onChange(description);
    setShowSuggestions(false);

    try {
      const place = new google.maps.places.Place({
        id: placeId,
      });

      const result = await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress'],
      });

      const placeData = result.place;

      if (placeData && placeData.addressComponents) {
        let streetNumber = '';
        let route = '';
        let city = '';
        let postalCode = '';
        let country = '';

        placeData.addressComponents.forEach((component) => {
          const types = component.types;
          if (types.includes('street_number')) streetNumber = component.longText || '';
          if (types.includes('route')) route = component.longText || '';
          if (types.includes('locality')) city = component.longText || city;
          if (types.includes('postal_town') && !city) city = component.longText || city; // Fallback France
          if (types.includes('administrative_area_level_2') && !city) city = component.longText || city;
          if (types.includes('postal_code')) postalCode = component.longText || '';
          if (types.includes('country')) country = component.shortText || '';
        });

        if (!route && !city) {
          console.warn('⚠️ [GooglePlacesSearch] Sélection ignorée (pas de route ni ville)');
          if (onChange) onChange('');
          return;
        }

        const street = route || placeData.formattedAddress || '';
        const fullAddress = route ? [streetNumber, route].filter(Boolean).join(' ') : placeData.formattedAddress || '';

        console.log('[GooglePlacesSearch] Adresse sélectionnée', { fullAddress, city, postalCode, country, streetNumber, route });
        onAddressSelect(fullAddress.trim(), city, postalCode, country, streetNumber, route);

        if (onChange && placeData.formattedAddress) {
          onChange(placeData.formattedAddress);
        }
        
        // Refresh session token
        // @ts-ignore
        const { AutocompleteSessionToken } = await google.maps.importLibrary("places");
        sessionToken.current = new AutocompleteSessionToken();
      }
    } catch (error) {
      console.error('❌ [GooglePlacesSearch] Erreur fetchFields', error);
    }
  };


  if (!isLoaded) {
    if (failed && enableManualFallback) {
      console.log('[GooglePlacesSearch] Fallback manuel actif (Google Maps indisponible)');
      return (
        <div className="relative">
          <input
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange && onChange(e.target.value)}
            className={className}
            autoComplete="street-address"
            name="address_autocomplete"
            id="address_autocomplete"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            data-lpignore="true"
            data-1p-ignore="true"
          />
          {!value && (
            <p className="text-xs text-foreground-secondary mt-1">
              {autocompleteText}
            </p>
          )}
        </div>
      );
    }

    return (
      <input
        type="text"
        placeholder="Chargement de Google Maps..."
        disabled
        className={`${className} opacity-50 cursor-not-allowed`}
      />
    );
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        className={className}
        autoComplete="street-address"
        name="address_autocomplete"
        id="address_autocomplete"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        data-lpignore="true"
        data-1p-ignore="true"
        onFocus={() => { if (value && suggestions.length > 0) setShowSuggestions(true); }}
        onBlur={() => {
           setTimeout(() => setShowSuggestions(false), 200);
        }}
      />
      
      {showSuggestions && suggestions.length > 0 && (
        <ul className="absolute z-50 w-full bg-surface border border-border rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto left-0">
          {suggestions.map((suggestion: any) => (
            <li
              key={suggestion.placePrediction.placeId}
              className="px-4 py-2 hover:bg-background-tertiary cursor-pointer text-sm text-foreground"
              onClick={() => handleSelectPrediction(suggestion.placePrediction.placeId, suggestion.placePrediction.text.text)}
            >
              {suggestion.placePrediction.text.text}
            </li>
          ))}
        </ul>
      )}

      {!value && (
        <p className="text-xs text-foreground-secondary mt-1">
          {autocompleteText}
        </p>
      )}
    </div>
  );
};

export default GooglePlacesSearch;
